import { db } from '../db/db.js';
import type { PantryKnowledge } from '../db/schema.js';
import { normalizeName } from './normalize.js';
import { clearCanonicalCache } from './canonical.js';
import seed from '../../data/pantry-knowledge-seed.json' with { type: 'json' };

interface SeedFile {
  version: string;
  lastUpdated: string;
  source: string;
  items: PantryKnowledge[];
}

const seedData = seed as SeedFile;

/**
 * Seed the PantryKnowledge table on first launch (ADR-009 hybrid storage).
 *
 * v0.1：write-once if table is empty。
 * v0.2+：偵測 schema 是否最新；若 sample row 缺新欄位 → clear + 重 seed
 *   - v0.2.0 條件：compareBaseUnit
 *   - v0.3.0 條件：commonPackageSizes
 *   不過 v0.3.0 把條件改成 seed JSON 的 version 字串比對（更穩；後續加欄位免改邏輯）
 *
 * v0.1.1 沒有 user-edit PantryKnowledge UI，所以重 seed 不會 wipe 使用者資料。
 * v0.4 加 user-edit 之前必須改成 diff-seed 邏輯（HANDOFF Watchout）。
 */
const SEED_VERSION_LS_KEY = 'mlh_seed_version';

export async function seedKnowledgeIfEmpty(): Promise<number> {
  const existing = await db.pantryKnowledge.count();
  const targetVersion = seedData.version;
  if (existing > 0) {
    const currentVersion =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(SEED_VERSION_LS_KEY)
        : null;
    if (currentVersion === targetVersion) return existing;
    await db.pantryKnowledge.clear();
  }
  await db.pantryKnowledge.bulkPut(seedData.items);
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(SEED_VERSION_LS_KEY, targetVersion);
  }
  clearCanonicalCache();
  return seedData.items.length;
}

/**
 * Look up a PantryKnowledge entry by normalized name match against name + aliases.
 * Returns the first match (alphabetically by id when multiple match).
 */
export async function lookupKnowledge(rawName: string): Promise<PantryKnowledge | null> {
  const target = normalizeName(rawName);
  const all = await db.pantryKnowledge.toArray();
  const matches = all.filter((k) => {
    if (normalizeName(k.name) === target) return true;
    return k.aliases.some((a) => normalizeName(a) === target);
  });
  matches.sort((a, b) => a.id.localeCompare(b.id));
  return matches[0] ?? null;
}

export function knowledgeSeedCount(): number {
  return seedData.items.length;
}
